import {ElementRef, Injectable, InjectionToken} from '@angular/core';
import {BehaviorSubject, fromEvent, Observable, Subject} from 'rxjs';
import {debounceTime, map, startWith} from 'rxjs/operators';
import {ScreenSize, ScreenSizeMaxWidth} from '../models/platform/screenSize';
import {WindowRef} from './windowRef';

export const DEVICE_SERVICE_TOKEN = new InjectionToken<DeviceService>('Root element for device width measurment');

@Injectable({
  providedIn: 'root'
})
export class DeviceService {

  private elementRef: ElementRef<any>;
  private screenSizeSubject = new BehaviorSubject(this._detectScreenSize());
  private readonly onElementSet = new Subject<void>();

  constructor(private readonly _windowRef: WindowRef) {
    fromEvent(window, 'resize').pipe(
        debounceTime(200), // Prevent too many emissions on rapid resize
        map(() => this._detectScreenSize()),
        startWith(void 0),
    ).subscribe(size => this.screenSizeSubject.next(size));
  }

  public screenSize$: Observable<any> = this.screenSizeSubject.asObservable();


  public get isIE11(): boolean {
    return !!this._windowRef.nativeWindow['MSInputMethodContext'] && !!this._windowRef.nativeWindow.document['documentMode'];
  }

  public get isWindowsDevice(): boolean {
    return window.navigator.platform.toLocaleUpperCase().indexOf('WIN') !== -1;
  }

  public get isMacOsDevice(): boolean {
    return window.navigator.platform.toLocaleUpperCase().indexOf('MAC') !== -1;
  }

  public get isMobileDevice(): boolean {
    return /Mobi|Android/i.test(navigator.userAgent);
  }

  public get isIOSDevice(): boolean {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
  }

  public get isTouchDevice(): boolean {
    const window = this._windowRef.nativeWindow;
    return ('ontouchstart' in window) ||
        !!this._windowRef.nativeWindow.navigator.maxTouchPoints;
  }

  public setRootElement(element: ElementRef) {
    this.elementRef = element;
    this.onElementSet.next();
  }

  public unsupportedBrowser(): boolean {
    return this.isIE11;
  }

  private _detectScreenSize(): ScreenSize {
    const innerWidth = window.innerWidth;
    if (innerWidth <= ScreenSizeMaxWidth.Mobile) {
      return ScreenSize.Mobile;
    }
    if (innerWidth <= ScreenSizeMaxWidth.SmallTablet) {
      return ScreenSize.SmallTablet;
    }
    if (innerWidth <= ScreenSizeMaxWidth.Tablet) {
      return ScreenSize.Tablet;
    }
    return ScreenSize.Desktop;
  }
}
